/**
 * @file pages/Logs.jsx
 * @description Request log viewer page.
 *
 * Features:
 *  - Filterable, paginated list of request log entries (list layout, not a
 *    wide table, so it doesn't overflow horizontally on narrow screens)
 *  - Filters: path (partial match), method, HTTP status code, date range
 *  - Expandable entry to view full request/response body
 *  - Metrics summary at the top (top 5 endpoints by request count)
 *
 * All authenticated roles can view logs.
 */

import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSocket } from '../context/SocketContext';

import JsonViewer from '../components/JsonViewer';
import ActionRunsPanel from '../components/ActionRunsPanel';
import apiClient from '../api/client';
import { listLogs, getMetrics, deleteLogs } from '../api/logs';

/** Selectable page sizes and the default. */
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 50;

/** HTTP method options for the filter dropdown. */
const METHODS = ['', 'GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

/** Initial filter state (all empty = no filtering). */
const EMPTY_FILTERS = {
  path:      '',
  remote_ip: '',
  body:      '',
  method:    '',
  status:    '',
  from:      '',
  to:        '',
};

/**
 * Returns a Bootstrap badge style for an HTTP status code.
 * @param {number} status
 */
function statusBadge(status) {
  if (!status) return null;
  const variant =
    status < 300 ? 'success' :
    status < 500 ? 'warning' :
    'danger';
  return <span className={`badge bg-${variant}`}>{status}</span>;
}

/**
 * Log viewer page component.
 */
export default function Logs() {
  const queryClient = useQueryClient();
  const { socket }   = useSocket();

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  // Applied filters are a separate state so filters are only submitted on form submit
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [page, setPage]       = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [expandedId, setExpandedId] = useState(null); // ID of the currently expanded row

  // Fetch paginated logs using the currently applied filters
  const { data: logsResult, isLoading, error } = useQuery({
    queryKey: ['logs', { ...appliedFilters, page, pageSize }],
    queryFn:  () => listLogs({ ...appliedFilters, page, limit: pageSize }),
    keepPreviousData: true, // keep old data visible while new page loads
  });

  // Fetch top endpoint metrics for the summary bar
  const { data: metrics = [] } = useQuery({
    queryKey: ['metrics'],
    queryFn:  () => getMetrics(),
  });

  // Deleting logs is destructive, so gate the button behind the admin role
  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn:  async () => (await apiClient.get('/api/users/me')).data,
    staleTime: 10 * 60 * 1000,
  });
  const isAdmin = currentUser?.role === 'admin';

  // Deletes logs matching the currently applied filters (or all, if none applied)
  const deleteMutation = useMutation({
    mutationFn: () => deleteLogs(appliedFilters),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['logs'] });
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
      setPage(1);
    },
  });

  // Live auto-update: refresh whenever the backend emits 'apiRequest:logged'.
  // Invalidating the ['logs'] prefix (not an exact key) refetches whatever
  // filters/page are currently active, so the applied filter is preserved.
  useEffect(() => {
    if (!socket) return;

    const handleApiRequestLogged = () => {
      queryClient.invalidateQueries({ queryKey: ['logs'] });
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
    };

    socket.on('apiRequest:logged', handleApiRequestLogged);
    return () => socket.off('apiRequest:logged', handleApiRequestLogged);
  }, [socket, queryClient]);

  const totalPages = logsResult ? Math.ceil(logsResult.total / pageSize) : 1;

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
    setPage(1); // reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  /** Toggles the expanded detail row for a log entry. */
  const handleExpandRow = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  /** Changes how many entries are shown per page, resetting back to page 1. */
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const logEntries = logsResult?.data ?? [];
  const hasActiveFilters = Object.values(appliedFilters).some(Boolean);

  /**
   * Confirms and deletes logs matching the currently applied filters.
   * Warns clearly when no filter is active, since that deletes every log entry.
   */
  const handleDeleteLogs = () => {
    const scope = hasActiveFilters
      ? 'the logs matching the current filter'
      : 'ALL logs (no filter is currently applied)';
    if (window.confirm(`Delete ${scope}? This cannot be undone.`)) {
      deleteMutation.mutate();
    }
  };

  return (
    <div className="page-content">
      <h1 className="h4 fw-bold mb-4" style={{ color: 'var(--color-primary)' }}>
        <i className="bi bi-journal-text me-2" aria-hidden="true" />
        Request Logs
      </h1>

      {/* ── Top endpoint metrics summary ──────────────────────────────────── */}
      {metrics.length > 0 && (
        <div className="card mb-4">
          <div
            className="card-header small fw-semibold"
            style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-light)' }}
          >
            <i className="bi bi-bar-chart me-2" aria-hidden="true" />
            Top Endpoints (by request count)
          </div>
          <div className="card-body p-0">
            {/* List layout instead of a wide table — avoids horizontal overflow
                on narrow screens by splitting path/method and metrics onto two lines. */}
            <ul className="list-group list-group-flush">
              {metrics.slice(0, 5).map((m, i) => (
                <li key={i} className="list-group-item">
                  <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                    <span
                      className="badge"
                      style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
                    >
                      {m.method}
                    </span>
                    <code className="small text-break">{m.path}</code>
                  </div>
                  <div className="d-flex flex-wrap gap-3 small text-muted">
                    <span><strong>{m.request_count.toLocaleString()}</strong> requests</span>
                    <span>avg <strong>{Math.round(m.avg_duration_ms)} ms</strong></span>
                    <span>max <strong>{m.max_duration_ms} ms</strong></span>
                    <span>in <strong>{(m.total_bytes_in / 1024).toFixed(1)} KB</strong></span>
                    <span>out <strong>{(m.total_bytes_out / 1024).toFixed(1)} KB</strong></span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* ── Filters ─────────────────────────────────────────────────────────── */}
      <div className="card mb-4">
        <div
          className="card-header small fw-semibold"
          style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-light)' }}
        >
          <i className="bi bi-funnel me-2" aria-hidden="true" />
          Filters
        </div>
        <div className="card-body">
          <form onSubmit={handleApplyFilters} className="row g-3 align-items-end">
            <div className="col-md-3">
              <label htmlFor="filter-path" className="form-label form-label-sm fw-semibold">Path</label>
              <input
                id="filter-path"
                type="text"
                className="form-control form-control-sm"
                name="path"
                value={filters.path}
                onChange={handleFilterChange}
                placeholder="/api/tokens"
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="filter-remote-ip" className="form-label form-label-sm fw-semibold">Remote IP</label>
              <input
                id="filter-remote-ip"
                type="text"
                className="form-control form-control-sm"
                name="remote_ip"
                value={filters.remote_ip}
                onChange={handleFilterChange}
                placeholder="203.0.113."
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="filter-body" className="form-label form-label-sm fw-semibold">Request Body</label>
              <input
                id="filter-body"
                type="text"
                className="form-control form-control-sm"
                name="body"
                value={filters.body}
                onChange={handleFilterChange}
                placeholder="text to search for"
              />
            </div>
            <div className="col-md-2">
              <label htmlFor="filter-method" className="form-label form-label-sm fw-semibold">Method</label>
              <select
                id="filter-method"
                className="form-select form-select-sm"
                name="method"
                value={filters.method}
                onChange={handleFilterChange}
              >
                <option value="">All</option>
                {METHODS.filter(Boolean).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label htmlFor="filter-status" className="form-label form-label-sm fw-semibold">Status</label>
              <input
                id="filter-status"
                type="number"
                className="form-control form-control-sm"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                placeholder="200"
                min="100"
                max="599"
              />
            </div>
            <div className="col-md-2">
              <label htmlFor="filter-from" className="form-label form-label-sm fw-semibold">From</label>
              <input
                id="filter-from"
                type="datetime-local"
                className="form-control form-control-sm"
                name="from"
                value={filters.from}
                onChange={handleFilterChange}
              />
            </div>
            <div className="col-md-2">
              <label htmlFor="filter-to" className="form-label form-label-sm fw-semibold">To</label>
              <input
                id="filter-to"
                type="datetime-local"
                className="form-control form-control-sm"
                name="to"
                value={filters.to}
                onChange={handleFilterChange}
              />
            </div>

            {/* Buttons get their own full-width row so they always have room */}
            <div className="col-12 d-flex justify-content-end gap-2">
              <button
                type="submit"
                className="btn btn-sm"
                style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
              >
                Apply
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={handleClearFilters}
              >
                Clear
              </button>
              {isAdmin && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleDeleteLogs}
                  disabled={deleteMutation.isPending}
                  title={hasActiveFilters ? 'Delete logs matching the current filter' : 'Delete ALL logs'}
                >
                  <i className="bi bi-trash me-1" aria-hidden="true" />
                  {deleteMutation.isPending ? 'Deleting…' : hasActiveFilters ? 'Delete Filtered' : 'Delete All'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {deleteMutation.isError && (
        <div className="alert alert-danger">{deleteMutation.error.message}</div>
      )}

      {/* ── Log list ──────────────────────────────────────────────────────── */}
      <div className="card">
        <div className="card-body p-0">
          {error ? (
            <div className="alert alert-danger m-3">{error.message}</div>
          ) : (
            <ul className="list-group list-group-flush">
              {isLoading && (
                <li className="list-group-item text-center py-4">
                  <div
                    className="spinner-border spinner-border-sm"
                    style={{ color: 'var(--color-primary)' }}
                    role="status"
                  >
                    <span className="visually-hidden">Loading…</span>
                  </div>
                </li>
              )}
              {!isLoading && logEntries.length === 0 && (
                <li className="list-group-item text-center text-muted py-4">
                  No log entries match the current filters.
                </li>
              )}
              {logEntries.map((row) => {
                const isExpanded = expandedId === row.id;
                return (
                  <li key={row.id} className="list-group-item">
                    {/* Header line: toggle, time, method, path, status */}
                    <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                      <button
                        className="btn btn-link btn-sm p-0"
                        onClick={() => handleExpandRow(row.id)}
                        aria-expanded={isExpanded}
                        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} log entry ${row.id}`}
                      >
                        <i
                          className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`}
                          aria-hidden="true"
                        />
                      </button>
                      <span className="text-muted small">{new Date(row.timestamp).toLocaleString()}</span>
                      <span
                        className="badge"
                        style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
                      >
                        {row.method}
                      </span>
                      <code className="small text-break">{row.path}</code>
                      <span className="ms-auto">{statusBadge(row.response_status)}</span>
                    </div>

                    {/* Meta line: remote IP, token, duration, bytes.
                        No left offset and text-break on IP/token — a fixed
                        margin plus unbreakable long values (e.g. token names)
                        pushed content past the row width on narrow screens. */}
                    <div className="d-flex flex-wrap gap-3 small text-muted">
                      <span className="text-break">
                        IP: {row.remote_ip ? <code className="small">{row.remote_ip}</code> : '—'}
                      </span>
                      <span className="text-break">
                        Token: {row.apiToken
                          ? <code className="small">{row.apiToken.name} ({row.apiToken.token_prefix}…)</code>
                          : '— (Entra ID)'}
                      </span>
                      <span>{row.duration_ms != null ? `${row.duration_ms} ms` : '—'}</span>
                      <span>{row.bytes_in ?? 0} B in / {row.bytes_out ?? 0} B out</span>
                    </div>

                    {/* Expanded detail: request/response bodies */}
                    {isExpanded && (
                      <div className="row g-3 mt-1">
                        <div className="col-md-6">
                          <p className="fw-semibold mb-1 small text-uppercase text-muted">Request Body</p>
                          <JsonViewer value={row.request_body} />
                        </div>
                        <div className="col-md-6">
                          <p className="fw-semibold mb-1 small text-uppercase text-muted">Response Body</p>
                          <JsonViewer value={row.response_body} />
                        </div>
                        <div className="col-12">
                          <p className="fw-semibold mb-1 small text-uppercase text-muted">Actions</p>
                          <ActionRunsPanel correlationId={row.correlation_id} />
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Pagination */}
        {logsResult && (
          <div className="card-footer d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span className="text-muted small">
              {logsResult.total.toLocaleString()} entries — page {page} of {totalPages}
            </span>

            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-2">
                <label htmlFor="page-size" className="text-muted small mb-0">Per page</label>
                <select
                  id="page-size"
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                  value={pageSize}
                  onChange={handlePageSizeChange}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>

              {totalPages > 1 && (
                <nav aria-label="Log page navigation">
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setPage(page - 1)} disabled={page <= 1}>
                        <i className="bi bi-chevron-left" aria-hidden="true" />
                      </button>
                    </li>
                    <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                        <i className="bi bi-chevron-right" aria-hidden="true" />
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
